import React, { JSX, useEffect, useState } from "react";
import { ButtonStyle } from "components/buttons/button/button";
import { Modal, ModalAction } from "components/modal/modal";
import { InputTypes } from "components/form/enumerations/input-types";
import { FormTextArea } from "components/form/form-textarea/form-textarea";
import { ToastManager } from "utilities/toast/toast-manager";
import { t } from "utilities/localization/t";
import { CheckboxInput } from "components/form/inputs/checkbox-input/checkbox-input";
import { CheckboxSize } from "components/form/inputs/checkbox-input/checkbox";
import { EnrollmentRecord } from "models/view-models/enrollments/enrollment-record";
import "./evaluation-exemption-modal.scss";

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface EvaluationExemptionModalProps {
    enrollmentToEdit: EnrollmentRecord;
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onSave?: (enrollment: EnrollmentRecord) => void;
    disabled: boolean;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "evaluation-exemption-modal";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const EvaluationExemptionModal: React.FC<EvaluationExemptionModalProps> = ({
    enrollmentToEdit,
    open,
    setOpen,
    onSave,
    disabled
}): JSX.Element => {
    
    const [exemption, setExemption] = useState<boolean>(enrollmentToEdit.evaluationExemptionGranted ?? false);
    const [exemptionNotes, setExemptionNotes] = useState<string>(enrollmentToEdit.evaluationExemptionNotes ?? "");

    const [dirty, setDirty] = useState(false);
    

    const [enrollment, setEnrollment] = useState<EnrollmentRecord>(enrollmentToEdit);

   useEffect(() => {
        const enrollment = enrollmentToEdit ?? new EnrollmentRecord();
        setEnrollment(enrollment);
    }, [enrollmentToEdit]);


    const modalActionArray: ModalAction[] = [
        {
            buttonText: t("cancel"),
            onClick: () => onModalCancel(),
            style: ButtonStyle.Secondary,
        },

        {
            buttonText: t("saveChanges"),
            disabled: disabled, 
            onClick: () => handleSave(),
            style: ButtonStyle.Primary,
        },
    ];


    const onModalCancel = (): void => {
        setExemption(enrollment.evaluationExemptionGranted ?? false);
        setExemptionNotes(enrollment.evaluationExemptionNotes ?? "");
        setDirty(false);
        setOpen(false);
    };

    const onModalClose = (): void => {
        setOpen(false);
    };

    const handleSave = async (): Promise<void> => {
        const updatedEnrollment = enrollment.with({
            evaluationExemptionGranted: exemption,
            evaluationExemptionNotes: exemptionNotes,
        });
        
        setEnrollment(updatedEnrollment);

        if (await onSave?.(updatedEnrollment)) {
            onModalClose();
        } else {
            ToastManager.error("failedToSaveExemption");
        }
    };

    const handleUpdateExemption = (isExempt: boolean): void => {
        setExemption(isExempt);
        setDirty(true);
    }

    const handleUpdateExemptionNote = (note: string): void => {
       setExemptionNotes(note);
       setDirty(true);
    }

    return (
        <Modal
            cssClassName={CSS_CLASS_NAME}
            isOpen={open}
            onModalClose={onModalClose}
            title={t("evaluationExemption")}
            actions={modalActionArray}
            modalStyle={""}>

            <div className="evaluation-exception">
                <CheckboxInput
                    checked={exemption}
                    disabled={disabled}
                    label={t("grantEvaluationExemption")}
                    id="evaluationExemptionGranted"
                    onChange={() => handleUpdateExemption(!exemption)}
                    size={CheckboxSize.Small}
                />
            </div>
            <FormTextArea
                ariaLabelledBy={t("changeLog")}
                formFieldName="changeLog"
                id="changeLog"
                label={t("changeLog")}
                onChange={(event) => handleUpdateExemptionNote(event.target.value)}
                placeholder={t("enterChangeLog")}
                required={true}
                rows={4}
                value={exemptionNotes}
                type={InputTypes.Text}
                disabled={disabled}
            />
        </Modal>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { EvaluationExemptionModal };

// #endregion Exports
